import React, { useState, useMemo } from 'react';
import { 
  XMarkIcon, 
  ExclamationTriangleIcon, 
  UserGroupIcon,
  CreditCardIcon,
  StarIcon
} from '@heroicons/react/24/outline';

// Critical-Only Alert Banner - No Warnings, Only When Over Limits
export function AlertBanner({ subscriptionInfo, organizationInfo, onUpgrade, onManageMembers, onUpdatePayment }) {
  const [dismissed, setDismissed] = useState(false);

  const alertInfo = useMemo(() => {
    if (!subscriptionInfo) return null;

    const now = new Date();
    const trialEndDate = subscriptionInfo.trialEndDate ? new Date(subscriptionInfo.trialEndDate) : null;
    const periodEndDate = subscriptionInfo.currentPeriodEnd ? new Date(subscriptionInfo.currentPeriodEnd) : null;
    const daysUntilTrialEnd = trialEndDate ? Math.ceil((trialEndDate - now) / (1000 * 60 * 60 * 24)) : 0;
    const daysUntilPeriodEnd = periodEndDate ? Math.ceil((periodEndDate - now) / (1000 * 60 * 60 * 24)) : 0;

    // Member limit calculations
    const currentMembers = subscriptionInfo?.currentMembers || 0;
    const maxMembers = subscriptionInfo?.maxMembers || 0;

    // Only show alerts when they're OVER limits - no warnings
    
    // Payment Failed - blocking access
    if (subscriptionInfo.status === 'past_due') {
      return {
        type: 'critical',
        icon: CreditCardIcon,
        title: 'Payment Failed',
        message: 'Your subscription payment failed. Update your payment method to maintain access.',
        action: 'Update Payment',
        actionHandler: onUpdatePayment,
        bgColor: 'from-red-600 to-red-700',
        borderColor: 'border-red-800'
      };
    }

    // Trial Expired - no longer have access
    if (subscriptionInfo.status === 'trialing' && daysUntilTrialEnd <= 0) {
      return {
        type: 'critical',
        icon: ExclamationTriangleIcon,
        title: 'Trial Access Expired',
        message: 'Subscribe to restore access to all features',
        action: 'Subscribe Now',
        actionHandler: onUpgrade,
        bgColor: 'from-red-600 to-red-700',
        borderColor: 'border-red-800'
      };
    }

    // Subscription Ended - no longer have access
    if (subscriptionInfo.status === 'canceled' && daysUntilPeriodEnd <= 0) {
      return {
        type: 'critical',
        icon: ExclamationTriangleIcon,
        title: 'Subscription Ended',
        message: 'Your subscription has ended. Reactivate to restore full access.',
        action: 'Reactivate',
        actionHandler: onUpgrade,
        bgColor: 'from-red-600 to-red-700',
        borderColor: 'border-red-800'
      };
    }

    // Over Member Limit - violating plan limits
    if (currentMembers > maxMembers && maxMembers > 0) {
      return {
        type: 'critical',
        icon: UserGroupIcon,
        title: 'Over Member Limit',
        message: `You have ${currentMembers} members but your ${subscriptionInfo.tier} plan allows ${maxMembers}. Upgrade or remove members.`,
        action: 'Upgrade Plan',
        actionHandler: onUpgrade,
        secondaryAction: 'Manage Members',
        secondaryHandler: onManageMembers,
        bgColor: 'from-red-600 to-red-700',
        borderColor: 'border-red-800'
      };
    }

    // Active Trial - show days remaining (info only)
    if (subscriptionInfo.status === 'trialing' && daysUntilTrialEnd > 0) {
      return {
        type: 'info',
        icon: StarIcon,
        title: `${daysUntilTrialEnd} Day${daysUntilTrialEnd !== 1 ? 's' : ''} of Full Access Remaining`,
        message: 'You\'re currently enjoying unlimited access to all premium features',
        action: 'Choose Plan',
        actionHandler: onUpgrade,
        bgColor: 'from-blue-600 to-indigo-700',
        borderColor: 'border-indigo-800'
      };
    }

    return null;
  }, [subscriptionInfo, onUpgrade, onManageMembers, onUpdatePayment]);

  if (!alertInfo || dismissed) return null;

  const { type, icon: Icon, title, message, action, actionHandler, secondaryAction, secondaryHandler, bgColor, borderColor } = alertInfo;

  return (
    <div className={`
      relative px-6 py-2 text-sm text-white border-b-2 shadow-lg
      bg-gradient-to-r ${bgColor} ${borderColor}
    `}>
      <div className="max-w-7xl mx-auto flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className={`
            p-2 rounded-full backdrop-blur-sm border
            ${type === 'critical' ? 'bg-red-500/20 border-red-400/30' : 'bg-blue-400/20 border-blue-300/30'}
          `}>
            <Icon className="h-5 w-5" />
          </div>
          
          <div className="flex flex-col sm:flex-row sm:items-center sm:gap-2">
            <span className="font-semibold">{title}</span>
            <span className="text-xs sm:text-sm opacity-90">{message}</span>
          </div>
        </div>

        <div className="flex items-center gap-3">
          {secondaryAction && (
            <button 
              onClick={secondaryHandler}
              className="px-3 py-1.5 rounded-lg font-medium text-xs transition-all duration-200 hover:scale-105 bg-white/10 hover:bg-white/20 border border-white/20 backdrop-blur-sm"
            >
              {secondaryAction}
            </button>
          )}
          
          <button 
            onClick={actionHandler}
            className={`
              px-4 py-2 rounded-lg font-semibold text-sm transition-all duration-200 hover:scale-105 shadow-md
              ${type === 'critical'
                ? 'bg-white text-red-600 hover:bg-red-50 border border-red-200' 
                : 'bg-white/10 text-white hover:bg-white/20 border border-white/20 backdrop-blur-sm'
              }
            `}
          >
            {action}
          </button>
          
          <button
            onClick={() => setDismissed(true)}
            className="p-1 rounded-full hover:bg-white/10 transition-colors duration-200"
          >
            <XMarkIcon className="h-5 w-5 opacity-70 hover:opacity-100" />
          </button>
        </div>
      </div>
    </div>
  );
}



// Demo component to show only critical alert states
export function AlertDemo() {
  const [selectedAlert, setSelectedAlert] = useState('active-trial');

  const mockData = {
    'active-trial': {
      subscription: { 
        status: 'trialing', 
        tier: 'pro', 
        trialEndDate: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString(),
        currentMembers: 5,
        maxMembers: 9999
      }
    },
    'trial-expired': {
      subscription: { 
        status: 'trialing', 
        tier: 'pro', 
        trialEndDate: new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString(),
        currentMembers: 5,
        maxMembers: 10
      }
    },
    'over-members': {
      subscription: { 
        status: 'active', 
        tier: 'starter',
        currentMembers: 15,
        maxMembers: 10
      }
    },
    'payment-failed': {
      subscription: { 
        status: 'past_due', 
        tier: 'pro',
        currentMembers: 5,
        maxMembers: 25
      }
    },
    'subscription-ended': {
      subscription: { 
        status: 'canceled', 
        tier: 'pro', 
        currentPeriodEnd: new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString(),
        currentMembers: 5,
        maxMembers: 25
      }
    },
    'no-alert': {
      subscription: { 
        status: 'active', 
        tier: 'pro',
        currentMembers: 5,
        maxMembers: 25
      }
    }
  };

  const current = mockData[selectedAlert];

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="p-6 bg-white border-b">
        <h1 className="text-2xl font-bold mb-4">Alert Banner System</h1>
        <p className="text-gray-600 mb-4">Shows critical alerts when over limits + active trial info</p>
        <div className="flex flex-wrap gap-2">
          {Object.keys(mockData).map(key => (
            <button
              key={key}
              onClick={() => setSelectedAlert(key)}
              className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                selectedAlert === key 
                  ? 'bg-blue-600 text-white' 
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              {key.replace('-', ' ').replace(/\b\w/g, l => l.toUpperCase())}
            </button>
          ))}
        </div>
      </div>

      <AlertBanner
        subscriptionInfo={current.subscription}
        onUpgrade={() => alert('Upgrade clicked')}
        onManageMembers={() => alert('Manage members clicked')}
        onUpdatePayment={() => alert('Update payment clicked')}
      />

      <div className="p-6">
        <h2 className="text-lg font-semibold mb-4">Current Alert Data:</h2>
        <pre className="bg-gray-100 p-4 rounded-lg text-sm overflow-auto">
          {JSON.stringify(current, null, 2)}
        </pre>
      </div>
    </div>
  );
}